import React from 'react';
import { Link } from 'react-router-dom';
import {
  Database, Target, Brain, Leaf, GitBranch, ShieldAlert, Network,
  ArrowRight, ArrowUpRight,
} from 'lucide-react';
import { Reveal } from '../pop/motion';

/**
 * ExecutiveSummarySection — McKinsey / BCG editorial standard.
 *
 * Functionality preserved:
 *   · 6 assessment cards still route via <Link to={path}> to the same paths.
 *   · Same data-testid pattern per assessment card.
 */
const ASSESSMENTS = [
  {
    n: '01',
    Icon: Database,
    name: 'Data Maturity',
    kicker: 'Data-driven excellence',
    description: 'Diagnose data governance, quality, architecture and analytics readiness against sector leaders.',
    path: '/data-maturity',
  },
  {
    n: '02',
    Icon: Target,
    name: 'Digital Maturity',
    kicker: 'Digital transformation',
    description: 'Benchmark digital strategy, infrastructure and customer-experience posture across the value chain.',
    path: '/digital-maturity',
  },
  {
    n: '03',
    Icon: Brain,
    name: 'AI Maturity',
    kicker: 'AI readiness',
    description: 'Assess AI strategy, data foundation, talent depth and governance discipline in one board-ready view.',
    path: '/ai-maturity',
  },
  {
    n: '04',
    Icon: Leaf,
    name: 'Sustainability / ESG',
    kicker: 'ESG excellence',
    description: 'Evaluate environmental, social and governance posture across 10 dimensions the board is asked about.',
    path: '/sustainability',
  },
  {
    n: '05',
    Icon: GitBranch,
    name: 'BPCM Assessment',
    kicker: 'Change management',
    description: 'Score the seven change-management levers that predict whether transformation actually sticks.',
    path: '/bpcm-assessment',
  },
  {
    n: '06',
    Icon: ShieldAlert,
    name: 'Cyber Security',
    kicker: 'Security posture',
    description: '30 controls across 6 domains, aligned with NIST CSF and ISO 27001 — audit-defensible in a week.',
    path: '/cyber-security',
  },
  {
    n: '07',
    Icon: Network,
    name: 'SCOR Diagnostic',
    kicker: 'Supply-chain performance',
    description: 'Assess supply-chain processes, KPI benchmarks and system readiness, with saved assessments and downloadable reports.',
    path: '/diagnostic',
  },
];

const ExecutiveSummarySection = () => {
  return (
    <section
      id="executive-summary"
      data-testid="executive-summary-section"
      className="relative py-24 sm:py-32 bg-[#0a1220] overflow-hidden"
    >
      {/* Subtle background — restrained, editorial */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        />
        <div className="absolute -top-20 left-1/4 w-[600px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* --------- Editorial masthead --------- */}
        <Reveal className="max-w-3xl mb-16 sm:mb-20">
          <div className="text-[10px] tracking-[0.32em] font-bold uppercase text-emerald-300 mb-6">
            The SAMANVAI Practice &nbsp;·&nbsp; Diagnostic Suite
          </div>
          <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight mb-6">
            Seven diagnostic lenses.
            <br />
            <span className="italic text-emerald-300">One board-ready view.</span>
          </h2>
          <p className="text-lg sm:text-xl text-white/70 leading-relaxed font-light max-w-2xl">
            Great transformation begins with an honest read of where you stand. SAMANVAI codifies
            <span className="text-white font-normal"> twenty-five years of CXO advisory</span> into seven
            diagnostics — each benchmarked, industry-contextualised, and delivered as insight your board
            can act on <span className="text-white font-normal">in weeks, not quarters</span>.
          </p>
        </Reveal>

        {/* --------- Editorial diagnostics grid --------- */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 sm:gap-y-14">
          {ASSESSMENTS.map((a, idx) => (
            <Reveal key={a.name} delay={idx * 60}>
              <Link
                to={a.path}
                data-testid={`assessment-card-${a.name.toLowerCase().replace(/\//g, '-').replace(/\s+/g, '-')}`}
                className="group block border-t border-white/15 pt-6 hover:border-emerald-300/70 transition-colors"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="font-serif text-white/25 text-4xl leading-none tabular-nums group-hover:text-emerald-300/50 transition-colors">
                    {a.n}
                  </div>
                  <a.Icon className="w-4 h-4 text-white/35 group-hover:text-emerald-300 transition-colors" />
                </div>
                <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-emerald-300/80 mb-2">
                  {a.kicker}
                </div>
                <h3 className="font-serif text-white text-2xl sm:text-[28px] leading-tight mb-3 group-hover:text-emerald-100 transition-colors">
                  {a.name}
                </h3>
                <p className="text-[15px] text-white/65 leading-relaxed mb-6">{a.description}</p>
                <div className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase font-bold text-white/60 group-hover:text-emerald-300 transition-colors">
                  Start diagnostic
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* --------- Editorial closing note --------- */}
        <Reveal delay={220}>
          <div className="mt-24 border-t border-white/10 pt-10 flex flex-col md:flex-row md:items-end gap-6 md:gap-12">
            <div className="max-w-md">
              <div className="text-[10px] tracking-[0.32em] font-bold uppercase text-white/45 mb-3">
                Our maturity diagnostics include
              </div>
              <p className="font-serif text-white text-2xl sm:text-3xl leading-snug">
                AI-authored executive brief, sector benchmarks, a 12-month roadmap and a
                <span className="italic text-emerald-300"> board-ready PowerPoint</span> — signed off in days.
              </p>
            </div>
            <div className="md:ml-auto">
              <a
                href="#offerings"
                data-testid="executive-summary-see-offerings"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/85 hover:text-white group border-b border-white/25 hover:border-white pb-1 transition-colors"
              >
                See adjacent platforms in the practice
                <ArrowUpRight className="w-4 h-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ExecutiveSummarySection;
